import {
    Component,
    computed,
    ElementRef,
    HostListener,
    input,
    signal,
    ViewChild,
    OnInit,
    OnDestroy,
    AfterViewInit,
    inject,
} from '@angular/core';
import { FlooriButtonComponent } from '../floori-button/floori-button.component';
import { FlooriIconComponent } from '../floori-icon/floori-icon.component';
import { AssetImageDto } from '../../models/asset-image.dto';
import { NgxImageZoomModule } from 'ngx-image-zoom';
import { PopupDirection } from '../../models/popup-direction';
import { TooltipDirective } from '../../directives/tooltip/tooltip.directive';
import { TranslationsModule } from '../../modules/translations/translations.module';
import { DeviceService } from '../../modules';

@Component({
    selector: 'floori-image-gallery',
    standalone: true,
    imports: [
        FlooriButtonComponent,
        FlooriIconComponent,
        NgxImageZoomModule,
        TooltipDirective,
        TranslationsModule,
    ],
    templateUrl: './floori-image-gallery.component.html',
    styleUrl: './floori-image-gallery.component.scss'
})
export class FlooriImageGalleryComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('imageList') imageList!: ElementRef<HTMLElement>;
    private resizeObserver?: ResizeObserver;
    private readonly deviceService = inject(DeviceService);

    readonly images = input.required<string[] | AssetImageDto[]>();
    readonly tooltipTranslationKey = input('room.gallery.tooltip');
    readonly tooltipOnMobile = input('room.gallery.tooltipMobile');
    readonly showZoomLens = input(true);

    readonly currentIndex = signal(0);
    readonly isMobile = signal(false);
    readonly isImageLoaded = signal(true);
    readonly tooltipDirection = PopupDirection.below;
    readonly lensSize = signal(200); // px

    readonly numberOfImages = computed(() => this.images().length);
    readonly showImageList = computed(() => this.images().length > 1);
    readonly currentImage = computed(() =>
        this.getMainImageUrl(this.images()[this.currentIndex()]),
    );
    readonly isFirstImage = computed(() => this.currentIndex() === 0);
    readonly isLastImage = computed(() => this.currentIndex() === this.numberOfImages() - 1);
    readonly tooltipValue = computed(() => this.tooltipTranslationKey());

    readonly shouldCenterList = signal<boolean>(false);

    // Mobile zoom functionality
    private readonly zoomScale = signal(1);
    private readonly translateX = signal(0);
    private readonly translateY = signal(0);
    private readonly initialDistance = signal(0);
    private readonly initialScale = signal(1);
    private readonly isDragging = signal(false);
    private readonly lastTouchX = signal(0);
    private readonly lastTouchY = signal(0);
    private readonly containerWidth = signal(0);
    private readonly containerHeight = signal(0);
    private readonly imageWidth = signal(0);
    private readonly imageHeight = signal(0);
    private readonly maxZoom = signal(5);

    readonly isZoomed = computed(() => this.zoomScale() > 1);
    readonly getTransform = computed(
        () =>
            `translate(${this.translateX()}px, ${this.translateY()}px) scale(${this.zoomScale()})`,
    );

    ngOnInit(): void {
        this.checkMobile();
    }

    ngAfterViewInit(): void {
        this.setupResizeObserver();
        this.checkImageListOverflow();
    }

    ngOnDestroy(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    onImageLoad(isLoaded: boolean): void {
        this.isImageLoaded.set(isLoaded);
    }

    onImageLoaded(event: Event): void {
        const img = event.target as HTMLImageElement;
        this.imageWidth.set(img.naturalWidth);
        this.imageHeight.set(img.naturalHeight);
        this.updateContainerDimensions();
    }

    getMainImageUrl(image: string | AssetImageDto): string {
        if (typeof image === 'string') {
            return image;
        }
        return image.large || image.medium || image.small || '';
    }

    getThumbnailUrl(image: string | AssetImageDto): string {
        if (typeof image === 'string') {
            return image;
        }
        return image.small || image.medium || image.large || '';
    }

    nextImage(): void {
        this.isImageLoaded.set(false);
        this.currentIndex.update(index => index + 1);
        this.resetZoom();
        this.scrollToCurrentImage();
    }

    previousImage(): void {
        this.isImageLoaded.set(false);
        this.currentIndex.update(index => index - 1);
        this.resetZoom();
        this.scrollToCurrentImage();
    }

    goToImage(index: number): void {
        this.isImageLoaded.set(false);
        this.currentIndex.set(index);
        this.resetZoom();
        this.scrollToCurrentImage();
    }

    onWheel(event: WheelEvent): void {
        event.preventDefault();
        const container = event.currentTarget as HTMLElement;
        container.scrollLeft += event.deltaY;
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent): void {
        if (event.key === 'ArrowRight' && !this.isLastImage()) {
            this.nextImage();
        } else if (event.key === 'ArrowLeft' && !this.isFirstImage()) {
            this.previousImage();
        }
    }

    @HostListener('window:resize')
    onResize(): void {
        this.checkMobile();
        this.updateContainerDimensions();
    }

    @HostListener('window:orientationchange')
    onOrientationChange(): void {
        // Wait for orientation change to complete
        setTimeout(() => {
            this.updateContainerDimensions();
        }, 100);
    }

    // Mobile zoom event handlers
    onTouchStart(event: TouchEvent): void {
        event.preventDefault();
        const touches = event.touches;

        if (touches.length === 1) {
            // Single touch - start dragging
            this.isDragging.set(true);
            this.lastTouchX.set(touches[0].clientX);
            this.lastTouchY.set(touches[0].clientY);
        } else if (touches.length === 2) {
            // Two touches - start pinch zoom
            this.isDragging.set(false);
            const distance = this.getDistance(touches[0], touches[1]);
            this.initialDistance.set(distance);
            this.initialScale.set(this.zoomScale());
        }
    }

    onTouchMove(event: TouchEvent): void {
        event.preventDefault();
        const touches = event.touches;

        if (touches.length === 1 && this.isDragging() && this.zoomScale() > 1) {
            this.handleSingleTouchMove(touches[0]);
        } else if (touches.length === 2) {
            this.handlePinchZoom(touches[0], touches[1]);
        }
    }

    onTouchEnd(event: TouchEvent): void {
        event.preventDefault();
        this.isDragging.set(false);
    }

    private getDistance(touch1: Touch, touch2: Touch): number {
        const dx = touch1.clientX - touch2.clientX;
        const dy = touch1.clientY - touch2.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }

    private resetZoom(): void {
        this.zoomScale.set(1);
        this.translateX.set(0);
        this.translateY.set(0);
        this.isDragging.set(false);

        // Recalculate container dimensions after a short delay to ensure DOM is updated
        setTimeout(() => {
            this.updateContainerDimensions();
        }, 100);
    }

    private handleSingleTouchMove(touch: Touch): void {
        // Single touch dragging (only when zoomed)
        const deltaX = touch.clientX - this.lastTouchX();
        const deltaY = touch.clientY - this.lastTouchY();

        // Calculate new position
        const newX = this.translateX() + deltaX;
        const newY = this.translateY() + deltaY;

        // Apply the translation
        this.translateX.set(newX);
        this.translateY.set(newY);

        // Constrain the panning to keep image within bounds
        this.constrainTranslation();

        this.lastTouchX.set(touch.clientX);
        this.lastTouchY.set(touch.clientY);
    }

    private handlePinchZoom(touch1: Touch, touch2: Touch): void {
        // Two touches - pinch zoom
        const distance = this.getDistance(touch1, touch2);
        const scale = (distance / this.initialDistance()) * this.initialScale();
        const clampedScale = Math.max(1, Math.min(this.maxZoom(), scale));

        this.zoomScale.set(clampedScale);

        // Reset translation when zooming out to 1
        if (clampedScale === 1) {
            this.translateX.set(0);
            this.translateY.set(0);
        } else {
            // Constrain existing translation to new zoom level
            this.constrainTranslation();
        }
    }

    private constrainTranslation(): void {
        const bounds = this.calculateBounds();
        this.translateX.set(Math.max(bounds.minX, Math.min(bounds.maxX, this.translateX())));
        this.translateY.set(Math.max(bounds.minY, Math.min(bounds.maxY, this.translateY())));
    }

    private calculateBounds(): { minX: number; maxX: number; minY: number; maxY: number } {
        const scale = this.zoomScale();
        if (scale <= 1) {
            return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
        }

        const containerW = this.containerWidth();
        const containerH = this.containerHeight();
        const imageW = this.imageWidth();
        const imageH = this.imageHeight();

        if (containerW === 0 || containerH === 0 || imageW === 0 || imageH === 0) {
            // Fallback to simple calculation if dimensions not available
            const maxOffset = (scale - 1) * 100;
            return {
                minX: -maxOffset,
                maxX: maxOffset,
                minY: -maxOffset,
                maxY: maxOffset,
            };
        }

        // Get the actual displayed image dimensions (with object-fit: contain)
        const displayedDimensions = this.getDisplayedImageDimensions();
        const displayedImageW = displayedDimensions.width;
        const displayedImageH = displayedDimensions.height;

        // Calculate the scaled image dimensions
        const scaledImageW = displayedImageW * scale;
        const scaledImageH = displayedImageH * scale;

        // Calculate the maximum allowed translation to keep image within bounds
        const maxTranslateX = Math.max(0, (scaledImageW - containerW) / 2);
        const maxTranslateY = Math.max(0, (scaledImageH - containerH) / 2);

        return {
            minX: -maxTranslateX,
            maxX: maxTranslateX,
            minY: -maxTranslateY,
            maxY: maxTranslateY,
        };
    }

    private updateContainerDimensions(): void {
        const container = document.querySelector('.mobile-zoom-container') as HTMLElement;
        if (container) {
            this.containerWidth.set(container.clientWidth);
            this.containerHeight.set(container.clientHeight);

            // If zoomed, constrain translation to new bounds
            if (this.isZoomed()) {
                this.constrainTranslation();
            }
        }
    }

    private scrollToCurrentImage(): void {
        if (!this.imageList) return;

        const container = this.imageList.nativeElement;
        const picWidth = 120; // px
        const containerWidth = container.clientWidth;
        const scrollPosition = this.currentIndex() * picWidth - containerWidth / 2 + picWidth / 2;

        container.scrollTo({
            left: Math.max(0, scrollPosition),
            behavior: 'smooth',
        });
    }

    private checkMobile(): void {
        const isMobile = this.deviceService.isMobileBySize || this.deviceService.isMobile;
        this.isMobile.set(isMobile);
    }

    private checkImageListOverflow(): void {
        if (!this.imageList?.nativeElement) return;

        const container = this.imageList.nativeElement;
        const hasOverflow = container.scrollWidth > container.clientWidth;
        this.shouldCenterList.set(!hasOverflow);
    }

    private setupResizeObserver(): void {
        this.resizeObserver = new ResizeObserver(() => {
            this.checkMobile();
            this.checkImageListOverflow();
        });

        if (this.imageList?.nativeElement) {
            this.resizeObserver.observe(this.imageList.nativeElement);
        }
    }

    private getDisplayedImageDimensions(): { width: number; height: number } {
        const containerW = this.containerWidth();
        const containerH = this.containerHeight();
        const imageW = this.imageWidth();
        const imageH = this.imageHeight();

        if (containerW === 0 || containerH === 0 || imageW === 0 || imageH === 0) {
            return { width: 0, height: 0 };
        }

        const containerAspectRatio = containerW / containerH;
        const imageAspectRatio = imageW / imageH;

        if (imageAspectRatio > containerAspectRatio) {
            // Image is wider than container - fit to width
            return {
                width: containerW,
                height: containerW / imageAspectRatio,
            };
        } else {
            // Image is taller than container - fit to height
            return {
                width: containerH * imageAspectRatio,
                height: containerH,
            };
        }
    }
}
